import { AppstoreOutlined, FileTextOutlined } from '@ant-design/icons'
import LegacyAdvicePrepRedirect from '@/features/advisory-ai/LegacyAdvicePrepRedirect'
import AdvisoryAI from '@/features/advisory-ai/org-list'
import AdvicePrepSupportConsole from '@/features/advisory-ai/support/page'
import AdvisoryAiSettingsPage from '@/features/advisory-ai/settings/page'
import AdvisoryAiCallPage from '@/features/advisory-ai/call/page'
import AdvisoryAiOrgCalls from '@/features/advisory-ai/calls/page'
import AdvisoryConsolidatedList from '@/features/advisory-ai/consolidated/list/page'
import AdvisoryConsolidatedPage from '@/features/advisory-ai/consolidated/page'
import AdvisoryDashboards from '@/pages/AdvisoryDashboards'
import AdvisoryMapping from '@/pages/AdvisoryMapping'
import AdvisoryMappingDetail from '@/pages/AdvisoryMappingDetail'
import ReportViewPage from '@/pages/ReportViewPage'
import SurveyForms from '@/pages/SurveyForms'
import { permissions, UserTypes } from '@/types/authorization'
import type { ProtectedRouteDescriptor } from '@/routes/types'

const advisoryAiAccess = { anyOf: [permissions.viewAdvisoryAI, permissions.updateAdvisoryAI] }

export const advisoryRouteDescriptors: ProtectedRouteDescriptor[] = [
  {
    key: 'advisory-dashboards',
    path: 'advisory-dashboards',
    element: <AdvisoryDashboards />,
    access: { anyOf: [permissions.viewReport, permissions.viewUserReports] },
    nav: {
      sectionKey: 'advisory',
      label: 'Dashboards',
      Icon: AppstoreOutlined,
      order: 10,
    },
  },
  {
    key: 'advisory-dashboards-view',
    path: 'advisory-dashboards/:reportId',
    element: <ReportViewPage source="advisory" />,
    access: { anyOf: [permissions.viewReport, permissions.viewUserReports] },
  },
  {
    key: 'advisorymapping',
    path: 'advisorymapping',
    element: <AdvisoryMapping />,
    access: { anyOf: [permissions.viewAdvisoryClient] },
    nav: {
      sectionKey: 'advisory',
      label: 'Advisory Mapping',
      Icon: FileTextOutlined,
      order: 20,
    },
    visibility: {
      clientFlags: { showAdvisoryMapping: true },
    },
  },
  {
    key: 'advisorymapping-detail',
    path: 'advisorymapping/detailed/:platformType/:platformId',
    element: <AdvisoryMappingDetail />,
    access: { anyOf: [permissions.viewAdvisoryClient] },
  },
  {
    key: 'surveyforms',
    path: 'surveyforms',
    element: <SurveyForms />,
    access: { anyOf: [permissions.viewSurveyForm] },
    nav: {
      sectionKey: 'practice',
      label: 'Survey Forms',
      Icon: FileTextOutlined,
      order: 30,
    },
    visibility: {
      when: (session) => !session.currentClientId,
    },
  },
  {
    key: 'advisoryai',
    path: 'adviceprep',
    element: <AdvisoryAI />,
    access: advisoryAiAccess,
    nav: {
      sectionKey: 'advisory',
      label: 'AdvicePrep',
      Icon: FileTextOutlined,
      order: 40,
    },
    visibility: {
      envFlags: { enableAdvisoryAIMenu: true },
    },
  },
  {
    key: 'advisoryai-legacy',
    path: 'advisoryai/*',
    element: <LegacyAdvicePrepRedirect />,
    access: advisoryAiAccess,
    visibility: {
      envFlags: { enableAdvisoryAIMenu: true },
    },
  },
  {
    key: 'advisoryai-call',
    path: 'adviceprep/org/:orgId/report/:callId',
    element: <AdvisoryAiCallPage />,
    access: advisoryAiAccess,
  },
  {
    key: 'advisoryai-consolidated',
    path: 'adviceprep/consolidated',
    element: <AdvisoryConsolidatedList />,
    access: advisoryAiAccess,
  },
  {
    key: 'advisoryai-consolidated-detail',
    path: 'adviceprep/consolidated/:groupId',
    element: <AdvisoryConsolidatedPage />,
    access: advisoryAiAccess,
  },
  {
    key: 'advisoryai-settings',
    path: 'adviceprep/settings',
    element: <AdvisoryAiSettingsPage />,
    access: advisoryAiAccess,
  },
  {
    key: 'advisoryai-calls',
    path: 'adviceprep/org/:id/calls',
    element: <AdvisoryAiOrgCalls />,
    access: advisoryAiAccess,
  },
  {
    key: 'advisoryai-support',
    path: 'adviceprep/support',
    element: <AdvicePrepSupportConsole />,
    access: advisoryAiAccess,
    // Gated on user type rather than a permission key, and deliberately not in
    // the sidebar — reached from a button on the AdvicePrep list.
    // `visibility.when` sends a typed URL to /unauthorized, so the hidden
    // button is not the only thing keeping non-admins out.
    visibility: {
      envFlags: { enableAdvisoryAIMenu: true },
      when: (session) => session.currentUserTypeId === UserTypes.Admin,
    },
  },
]
